/**
 * @prettier
 */

import { useState, useRef, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { useTranslation, Trans } from "react-i18next";

// Actions
import { submissionStarted } from "../../state/actions/submissionStateActions";
import { getCompanyName, isInIframe } from "../../services/shared/helpers";

// Components
import Header from "../../components/Header";
import CustomButton from "../../components/CustomButton";
import Navigator from "../../components/Navigator";

// Images
import logo from "../../assets/img/idpal_logo.svg";
import StartIconAnimated from "../../assets/svg/startScreenAnimatedIcon.svg";

// Alt Text
import { imageAlt } from "../../config/accessabilityRules";

const LinkText = ({ linkTo = "#", title = "", ...props }) => {
    return (
        <a
            href={linkTo}
            target="_blank"
            title={title}
            aria-label={{ title }}
            rel="noreferrer"
        >
            {props.children}
        </a>
    );
};

const StartPage = ({
    branding = null,
    submissionStarted,
    submission,
    policies,
}) => {
    const { t } = useTranslation();
    const primaryFocusRef = useRef();
    const [showIdPoweredByIDPal, setShowIdPoweredByIDPal] = useState(false);
    const [showCustomClientsLogo, setShowCustomClientsLogo] = useState(false);
    const [navigationState, setNavigationState] = useState({ action: "load" });
    const customClientsLogo = branding?.logo || null;
    const handleClick = () => {
        setNavigationState({
            action: "next",
        });
        submissionStarted();
    };

    const renderPoweredByIDPal = () => (
        <div className={"identity-mark-section"}>
            <p className="identity-mark-text">{t("idpal_powered_by")}</p>
            <img className="small-logo" src={logo} alt="ID-Pal Logo" />
        </div>
    );

    const customLogo = useMemo(() => {
        if (branding?.logo) {
            return `data:image/png;base64,${branding.logo}`;
        } else {
            return null;
        }
    }, [branding?.logo]);

    useEffect(() => {
        primaryFocusRef.current.focus();
        if (branding?.whitelabel === 0) {
            setShowIdPoweredByIDPal(true);
        }
        // Show custom client's logo if app is not opened in iframe
        if (customClientsLogo && !isInIframe()) {
            setShowCustomClientsLogo(true);
        }
        return () => null;
    }, [branding?.whitelabel, customClientsLogo, primaryFocusRef]);

    useEffect(() => {
        submission.new
            ? (document.title = t("idpal_doc_title_start"))
            : (document.title = t("idpal_doc_title_continue"));
        return () => null;
    }, [submission.new, t]);

    return (
        <div className="root-layout-container start-page">
            <Header showLanguageSelect isLogoHidden={showCustomClientsLogo} />
            <section className="content-wrapper">
                <div className="start-logo-container">
                    {showCustomClientsLogo ? (
                        <img
                            alt={imageAlt.startLogo}
                            className="custom-logo-image"
                            src={customLogo}
                        />
                    ) : (
                        <object
                            className="svg-animation-container"
                            data={StartIconAnimated}
                            aria-label={imageAlt.startLogo}
                        />
                    )}
                </div>
                <div className="start-content-container">
                    <div className="start-content">
                        <h1
                            ref={primaryFocusRef}
                            tabIndex={-1}
                            className="start-text-container"
                        >
                            <Trans
                                i18nKey="idpal_start_screen_default"
                                values={{
                                    companyName: getCompanyName(branding.name),
                                }}
                                components={{
                                    br: <br />,
                                }}
                            />
                        </h1>
                        <p className="start-privacy-policy-text">
                            <Trans
                                i18nKey="idpal_terms_and_conditions_warning"
                                values={{
                                    sessionState: submission.new
                                        ? t("idpal_start").toLowerCase()
                                        : t("idpal_continue").toLowerCase(),
                                }}
                                components={{
                                    link1: (
                                        <LinkText
                                            linkTo={
                                                policies.terms_and_conditions
                                            }
                                            title={"Terms and Conditions"}
                                        />
                                    ),
                                    link2: (
                                        <LinkText
                                            linkTo={policies.privacy_policy}
                                            title={"here"}
                                        />
                                    ),
                                }}
                            />
                        </p>
                    </div>

                    <div className="start-cta-container">
                        <CustomButton
                            label={
                                submission.new
                                    ? t("idpal_start")
                                    : t("idpal_continue")
                            }
                            id="start-cta-button"
                            handleClick={handleClick}
                            aria-label={
                                submission.new
                                    ? t("idpal_start")
                                    : t("idpal_continue")
                            }
                        />
                        {showIdPoweredByIDPal && renderPoweredByIDPal()}
                    </div>
                </div>
            </section>
            <Navigator
                page={"start_screen"}
                action={navigationState.action}
                propsToPass={null}
            />
        </div>
    );
};

function mapStateToProps(state) {
    return {
        submission: state.submissionState.details,
        branding: state.config.profile.data.company_branding,
        policies: state.config.profile.data.policies,
    };
}

function mapDispatchToProps(dispatch) {
    return bindActionCreators({ submissionStarted }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(StartPage);
